import { gql, type TypedDocumentNode } from "@apollo/client";
import { type GQL } from "@binale-tech/shared";

export const documentCreate: TypedDocumentNode<
    Pick<GQL.IMutation, "documentCreate">,
    GQL.IMutationDocumentCreateArgs
> = gql`
    mutation documentCreate($input: DocumentCreateInput!) {
        documentCreate(input: $input)
    }
`;

export const documentsUpdate: TypedDocumentNode<
    Pick<GQL.IMutation, "documentsUpdate">,
    GQL.IMutationDocumentsUpdateArgs
> = gql`
    mutation documentsUpdate($input: [DocumentUpdateInput!]!) {
        documentsUpdate(input: $input)
    }
`;

export const documentsDelete: TypedDocumentNode<
    Pick<GQL.IMutation, "documentsDelete">,
    GQL.IMutationDocumentsDeleteArgs
> = gql`
    mutation documentsDelete($companyId: ID!, $ids: [ID!]!) {
        documentsDelete(companyId: $companyId, ids: $ids) {
            hardDeleted
            movedToTrash
            keptAttached
        }
    }
`;

export const documentsTypeUpdate: TypedDocumentNode<
    Pick<GQL.IMutation, "documentsTypeUpdate">,
    GQL.IMutationDocumentsTypeUpdateArgs
> = gql`
    mutation documentsTypeUpdate($input: [DocumentTypeUpdateInput!]!) {
        documentsTypeUpdate(input: $input)
    }
`;

export const documentSubTypeCreate: TypedDocumentNode<
    Pick<GQL.IMutation, "documentSubTypeCreate">,
    GQL.IMutationDocumentSubTypeCreateArgs
> = gql`
    mutation documentSubTypeCreate($input: DocumentSubTypeCreateInput!) {
        documentSubTypeCreate(input: $input) {
            id
            name
            sortOrder
            icon
        }
    }
`;

export const documentSubTypeUpdate: TypedDocumentNode<
    Pick<GQL.IMutation, "documentSubTypeUpdate">,
    GQL.IMutationDocumentSubTypeUpdateArgs
> = gql`
    mutation documentSubTypeUpdate($input: DocumentSubTypeUpdateInput!) {
        documentSubTypeUpdate(input: $input) {
            id
            name
            sortOrder
            icon
        }
    }
`;

export const documentSubTypeDelete: TypedDocumentNode<
    Pick<GQL.IMutation, "documentSubTypeDelete">,
    GQL.IMutationDocumentSubTypeDeleteArgs
> = gql`
    mutation documentSubTypeDelete($input: DocumentSubTypeDeleteInput!) {
        documentSubTypeDelete(input: $input)
    }
`;

export const dmsDocumentBankingBind: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsDocumentBankingBind">,
    GQL.IMutationDmsDocumentBankingBindArgs
> = gql`
    mutation dmsDocumentBankingBind($input: DMSDocumentBankingBindInput!) {
        dmsDocumentBankingBind(input: $input)
    }
`;

export const dmsDocumentCashRegisterBind: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsDocumentCashRegisterBind">,
    GQL.IMutationDmsDocumentCashRegisterBindArgs
> = gql`
    mutation dmsDocumentCashRegisterBind($input: DMSCashRegisterBindInput!) {
        dmsDocumentCashRegisterBind(input: $input)
    }
`;

export const dmsDocumentAccountingBind: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsDocumentAccountingBind">,
    GQL.IMutationDmsDocumentAccountingBindArgs
> = gql`
    mutation dmsDocumentAccountingBind($input: AccountingBindInput!) {
        dmsDocumentAccountingBind(input: $input)
    }
`;

export const documentReview: TypedDocumentNode<
    Pick<GQL.IMutation, "documentReview">,
    GQL.IMutationDocumentReviewArgs
> = gql`
    mutation documentReview($input: DocumentReviewInput!) {
        documentReview(input: $input)
    }
`;

export const documentPaymentStatus: TypedDocumentNode<
    Pick<GQL.IMutation, "documentPaymentStatus">,
    GQL.IMutationDocumentPaymentStatusArgs
> = gql`
    mutation documentPaymentStatus($input: DocumentPaymentStatusInput!) {
        documentPaymentStatus(input: $input)
    }
`;

export const dmsDocumentAiLog: TypedDocumentNode<
    Pick<GQL.IQuery, "dmsDocumentAiLog">,
    GQL.IQueryDmsDocumentAiLogArgs
> = gql`
    query dmsDocumentAiLog($companyId: ID!, $documentId: ID!) {
        dmsDocumentAiLog(companyId: $companyId, documentId: $documentId)
    }
`;

export const dmsBewirtungSave: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsBewirtungSave">,
    GQL.IMutationDmsBewirtungSaveArgs
> = gql`
    mutation dmsBewirtungSave($companyId: ID!, $documentId: ID!, $input: DmsBewirtungInput!) {
        dmsBewirtungSave(companyId: $companyId, documentId: $documentId, input: $input) {
            templateVersion
            kind
            date
            place
            participants
            occasion
            amountCents
            tipCents
            host
            createdBy
            createdAt
            confirmedBy
            confirmedAt
            lock {
                lockedBy
                lockedAt
                signature
                keyVersion
            }
        }
    }
`;

export const dmsBewirtungDelete: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsBewirtungDelete">,
    GQL.IMutationDmsBewirtungDeleteArgs
> = gql`
    mutation dmsBewirtungDelete($companyId: ID!, $documentId: ID!) {
        dmsBewirtungDelete(companyId: $companyId, documentId: $documentId)
    }
`;

export const dmsBewirtungSignAndLock: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsBewirtungSignAndLock">,
    GQL.IMutationDmsBewirtungSignAndLockArgs
> = gql`
    mutation dmsBewirtungSignAndLock($companyId: ID!, $documentId: ID!) {
        dmsBewirtungSignAndLock(companyId: $companyId, documentId: $documentId) {
            lock {
                lockedBy
                lockedAt
                signature
                keyVersion
            }
        }
    }
`;

export const dmsBewirtungPublicKey: TypedDocumentNode<
    Pick<GQL.IQuery, "dmsBewirtungPublicKey">,
    GQL.IQueryDmsBewirtungPublicKeyArgs
> = gql`
    query dmsBewirtungPublicKey($keyVersion: String!) {
        dmsBewirtungPublicKey(keyVersion: $keyVersion)
    }
`;

export const dmsDocumentAVISAggregations: TypedDocumentNode<
    Pick<GQL.IQuery, "dmsDocumentAVISAggregations">,
    GQL.IQueryDmsDocumentAvisAggregationsArgs
> = gql`
    query dmsDocumentAVISAggregations($companyId: ID!, $documentId: ID!) {
        dmsDocumentAVISAggregations(companyId: $companyId, documentId: $documentId) {
            id
            documentAVISList {
                id
                companyId
                documentId
                paymentAmount
                discountAmount
                paymentDate
                dmsDocumentAVISAggregationId
                createdAt
            }
            documentAVISAggregations {
                id
                companyId
                bankId
                bankPaymentId
                cashRegisterId
                bankingTransactionId
                cashRegisterTransactionId
                amount
                isLocked
                createdAt
                documentAVISList {
                    id
                    companyId
                    documentId
                    paymentAmount
                    discountAmount
                    paymentDate
                    dmsDocumentAVISAggregationId
                    createdAt
                }
            }
        }
    }
`;

export const dmsDocumentsAVISList: TypedDocumentNode<
    Pick<GQL.IQuery, "dmsDocumentsAVISList">,
    GQL.IQueryDmsDocumentsAvisListArgs
> = gql`
    query dmsDocumentsAVISList($companyId: ID!, $documentIds: [ID!]!) {
        dmsDocumentsAVISList(companyId: $companyId, documentIds: $documentIds) {
            id
            companyId
            documentId
            paymentAmount
            discountAmount
            paymentDate
            dmsDocumentAVISAggregationId
            createdAt
        }
    }
`;

export const dmsDocumentAVISUpsert: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsDocumentAVISUpsert">,
    GQL.IMutationDmsDocumentAvisUpsertArgs
> = gql`
    mutation dmsDocumentAVISUpsert($companyId: ID!, $aggregationId: ID!, $input: DmsDocumentAVISInput!) {
        dmsDocumentAVISUpsert(companyId: $companyId, aggregationId: $aggregationId, input: $input) {
            id
            companyId
            documentId
            paymentAmount
            discountAmount
            paymentDate
            dmsDocumentAVISAggregationId
            createdAt
        }
    }
`;

export const dmsDocumentAVISDelete: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsDocumentAVISDelete">,
    GQL.IMutationDmsDocumentAvisDeleteArgs
> = gql`
    mutation dmsDocumentAVISDelete($companyId: ID!, $id: ID!) {
        dmsDocumentAVISDelete(companyId: $companyId, id: $id)
    }
`;

export const dmsDocumentAVISAggregationUpsert: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsDocumentAVISAggregationUpsert">,
    GQL.IMutationDmsDocumentAvisAggregationUpsertArgs
> = gql`
    mutation dmsDocumentAVISAggregationUpsert($companyId: ID!, $input: DmsDocumentAVISAggregationInput!) {
        dmsDocumentAVISAggregationUpsert(companyId: $companyId, input: $input) {
            id
            companyId
            bankId
            bankPaymentId
            cashRegisterId
            bankingTransactionId
            cashRegisterTransactionId
            amount
            isLocked
            createdAt
            documentAVISList {
                id
                companyId
                documentId
                paymentAmount
                discountAmount
                paymentDate
                dmsDocumentAVISAggregationId
                createdAt
            }
        }
    }
`;

export const dmsDocumentAVISAggregation: TypedDocumentNode<
    Pick<GQL.IQuery, "dmsAVISAggregation">,
    GQL.IQueryDmsAvisAggregationArgs
> = gql`
    query dmsAVISAggregation($companyId: ID!, $aggregationId: ID!) {
        dmsAVISAggregation(companyId: $companyId, aggregationId: $aggregationId) {
            id
            companyId
            bankId
            bankPaymentId
            cashRegisterId
            bankingTransactionId
            cashRegisterTransactionId
            amount
            isLocked
            createdAt
            documentAVISList {
                id
                companyId
                documentId
                paymentAmount
                discountAmount
                paymentDate
                dmsDocumentAVISAggregationId
                createdAt
            }
        }
    }
`;

export const dmsDocumentAVISAggregationLock: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsDocumentAVISAggregationLock">,
    GQL.IMutationDmsDocumentAvisAggregationLockArgs
> = gql`
    mutation dmsDocumentAVISAggregationLock($companyId: ID!, $id: ID!) {
        dmsDocumentAVISAggregationLock(companyId: $companyId, id: $id)
    }
`;

export const dmsDocumentAVISAggregationDelete: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsDocumentAVISAggregationDelete">,
    GQL.IMutationDmsDocumentAvisAggregationDeleteArgs
> = gql`
    mutation dmsDocumentAVISAggregationDelete($companyId: ID!, $id: ID!) {
        dmsDocumentAVISAggregationDelete(companyId: $companyId, id: $id)
    }
`;

export const dmsAVISAggregations: TypedDocumentNode<
    Pick<GQL.IQuery, "dmsAVISAggregations">,
    GQL.IQueryDmsAvisAggregationsArgs
> = gql`
    query dmsAVISAggregations($companyId: ID!) {
        dmsAVISAggregations(companyId: $companyId) {
            id
            companyId
            bankId
            bankPaymentId
            cashRegisterId
            bankingTransactionId
            cashRegisterTransactionId
            amount
            isLocked
            createdAt
            documentAVISList {
                id
                companyId
                documentId
                paymentAmount
                discountAmount
                paymentDate
                dmsDocumentAVISAggregationId
                createdAt
            }
        }
    }
`;
